import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMessageSquare, FiX, FiSend, FiMinimize2 } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar } from '../ui/avatar';
import { Loader2 } from 'lucide-react';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
}

const ROBOT_AVATARS = [
  "🤖", "🦾", "👾", "🛸", "🤩", "🤓", "🧠", "🦿", "🪐", "🎉"
];
const getRandomRobot = () => ROBOT_AVATARS[Math.floor(Math.random() * ROBOT_AVATARS.length)];

const GREETINGS = [
  "How can I help you?",
  "What can I do for you, superstar?",
  "Need a hand? 🖐️",
  "Ask me anything!",
  "Ready to chat! 😎",
  "What's up?",
  "How can I make your day awesome?",
  "Your wish is my command! 🧞‍♂️",
  "Let's make event magic! 🎉",
  "What brings you here today?",
  "Hit me with your best question!",
  "How can I assist your event adventure?",
  "Feeling lucky? Try me! 🍀",
  "Let's get this party started! 🥳",
  "Eventory at your service!"
];
const getRandomGreeting = () => GREETINGS[Math.floor(Math.random() * GREETINGS.length)];

const Chatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [greeting, setGreeting] = useState(getRandomGreeting());
  const [robot, setRobot] = useState(getRandomRobot());
  const [avatarAnim, setAvatarAnim] = useState<'idle' | 'wave' | 'spin'>('idle');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { messages, isLoading, sendMessage } = useChatbot();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      setGreeting(getRandomGreeting());
      setRobot(getRandomRobot());
      setAvatarAnim('idle');
    }
  }, [isOpen, isMinimized]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const userMessage = input.trim();
    setInput('');
    await sendMessage(userMessage);
  };

  const openChat = () => {
    setIsOpen(true);
    setIsMinimized(false);
    setAvatarAnim('idle');
  };
  const closeChat = () => {
    setAvatarAnim('wave');
    setIsOpen(false);
    setIsMinimized(false);
    setTimeout(() => setAvatarAnim('idle'), 400);
  };
  const minimizeChat = () => {
    setIsMinimized(true);
  };

  const avatarAnimClass =
    avatarAnim === 'wave'
      ? 'animate-avatar-wave'
      : avatarAnim === 'spin'
      ? 'animate-spin-fast'
      : '';

  return (
    <div className={cn("fixed z-50", isMobile && isOpen ? "inset-0" : "bottom-4 right-4")}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="chatbot-window"
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className={cn(
              "bg-gradient-to-br from-yellow-50 via-white to-pink-50 rounded-2xl shadow-2xl border border-gray-200 w-[380px]",
              isMinimized ? "h-[60px]" : "h-[600px]",
              isMobile && "chatbot-window-mobile"
            )}
            style={{ filter: 'drop-shadow(0 8px 32px #fbc2eb88)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-100 to-pink-100 rounded-t-2xl">
              <div className="flex items-center gap-2">
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1, transition: { type: 'spring', stiffness: 400, damping: 18 } }}
                  exit={{ scale: 0.5, opacity: 0, transition: { duration: 0.22, ease: 'easeIn' } }}
                  className={cn("h-10 w-10 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-400 to-pink-400 shadow-md border-2 border-white", avatarAnimClass)}
                  key={robot + greeting + isOpen}
                >
                  <span className="text-white text-xl" aria-label="Robot avatar">{robot}</span>
                </motion.div>
                <div>
                  <h3 className="font-bold text-lg font-[Comic Sans MS, Comic Sans, cursive] text-purple-700">Eventory Assistant</h3>
                  <p className="text-xs text-gray-500 transition-all animate-fade-in">{greeting}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={minimizeChat}
                  className="h-8 w-8 transition-transform hover:scale-110 hover:bg-purple-100 focus:ring-2 focus:ring-purple-300"
                  aria-label="Minimize chat"
                >
                  <FiMinimize2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeChat}
                  className="h-8 w-8 transition-transform hover:scale-110 hover:bg-pink-100 focus:ring-2 focus:ring-pink-300"
                  aria-label="Close chat"
                >
                  <FiX className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Chat Messages */}
            {!isMinimized && (
              <ScrollArea className={cn("h-[calc(600px-8rem)] p-4", isMobile && "chatbot-scroll-area-mobile")}>
                <div className="space-y-4">
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={cn(
                        "flex gap-3 items-end",
                        message.role === 'user' ? 'justify-end' : 'justify-start'
                      )}
                    >
                      {message.role === 'assistant' && (
                        <div className="h-8 w-8 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-400 to-pink-400 shadow-md border-2 border-white">
                          <span className="text-white text-lg">🤖</span>
                        </div>
                      )}
                      <div
                        className={cn(
                          "prose prose-sm rounded-2xl px-4 py-2 max-w-[80%] shadow-md",
                          "prose-p:mt-0 prose-p:mb-2 last:prose-p:mb-0",
                          "prose-ul:m-0 prose-ul:pl-4",
                          "prose-li:m-0",
                          message.role === 'user'
                            ? 'bg-gradient-to-br from-blue-400 to-purple-400 text-white rounded-br-md prose-strong:text-white'
                            : 'bg-gradient-to-br from-yellow-200 to-pink-200 text-gray-900 rounded-bl-md prose-strong:text-gray-900'
                        )}
                      >
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                      </div>
                      {message.role === 'user' && (
                        <div className="h-8 w-8 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-purple-400 shadow-md border-2 border-white">
                          <span className="text-white text-lg">🧑</span>
                        </div>
                      )}
                    </motion.div>
                  ))}
                  {isLoading && (
                    <div className="flex gap-3 items-end">
                      <div className="h-8 w-8 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-400 to-pink-400 shadow-md border-2 border-white">
                        <span className="text-white text-lg">🤖</span>
                      </div>
                      <div className="bg-gradient-to-br from-yellow-200 to-pink-200 rounded-2xl px-4 py-2 shadow-md flex items-center">
                        <span className="flex space-x-1">
                          <span className="animate-bounce inline-block w-2 h-2 bg-purple-400 rounded-full" style={{ animationDelay: '0s' }}></span>
                          <span className="animate-bounce inline-block w-2 h-2 bg-pink-400 rounded-full" style={{ animationDelay: '0.1s' }}></span>
                          <span className="animate-bounce inline-block w-2 h-2 bg-yellow-400 rounded-full" style={{ animationDelay: '0.2s' }}></span>
                        </span>
                        <span className="ml-2 text-xs text-gray-500">AI is typing…</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
            )}

            {/* Input Area */}
            {!isMinimized && (
              <form onSubmit={handleSubmit} className="p-4 border-t bg-gradient-to-r from-purple-100 to-pink-100 rounded-b-2xl">
                <div className="flex gap-2">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 rounded-full border-2 border-purple-200 focus:border-pink-400"
                    disabled={isLoading}
                  />
                  <Button type="submit" disabled={isLoading || !input.trim()} className="rounded-full bg-gradient-to-br from-purple-400 to-pink-400 text-white shadow-md hover:from-pink-400 hover:to-purple-400">
                    <FiSend className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Toggle Button */}
      {!isOpen && (
        <motion.button
          key="chatbot-toggle"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{
            scale: 1.22,
            rotate: [0, 12, -12, 0],
            boxShadow: '0 16px 48px 0 rgba(186, 85, 211, 0.55)',
            filter: 'brightness(1.18) saturate(1.3) drop-shadow(0 0 16px #fbc2ebcc)'
          }}
          whileTap={{ scale: 0.95 }}
          onClick={openChat}
          aria-label="Open chat assistant"
          className={cn(
            "relative p-0.5 rounded-full shadow-2xl focus:outline-none focus:ring-4 focus:ring-pink-300 group",
            isMobile && "chatbot-toggle-button-mobile"
          )}
          style={{
            background: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
            boxShadow: '0 4px 24px 0 rgba(186, 85, 211, 0.25)',
          }}
        >
          <span className="absolute inset-0 rounded-full animate-pulse bg-gradient-to-br from-pink-300 via-purple-300 to-yellow-200 opacity-60 blur-lg z-0" />
          <span className="relative flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-400 via-pink-400 to-yellow-300 shadow-lg border-4 border-white group-hover:scale-105 transition-transform">
            <span className="text-4xl select-none" role="img" aria-label="Chatbot">💬</span>
          </span>
        </motion.button>
      )}
    </div>
  );
};

export default Chatbot; 